<template>
  <div>
    <!-- Currency selection -->
    <button
      v-if="iconComponent"
      class="flex justify-center items-center gap-x-2 border border-gray-300 text-sm py-2 px-2.5 rounded-lg hover:shadow transition duration-200"
      :class="{ 'xs:px-4': !isCurrencySet }"
      @click="isModalOpen = true"
    >
      <span v-if="!isCurrencySet" class="w-5 h-5 hidden xs:block">
        <component
          :is="iconComponent"
          :alt="iconAltText"
          aria-hidden
          class="w-5 h-5 flex-shrink-0"
        />
      </span>

      <span
        class="no-translate text-sm"
        :class="[
          isCurrencySet ? 'xs:border-l-0 xs:pl-0' : 'xs:border-l-2 xs:pl-2',
        ]"
      >
        {{ displayLanguage }}
      </span>
    </button>

    <!-- Currency selection modal -->
    <CurrencyAndLanguageSelectionModal
      :selected-currency="currency"
      :selected-language="language"
      :active="isModalOpen"
      :currency-options="currencyOptions"
      :language-options="languageOptions"
      :currency-title="currencySelectionTitle"
      :language-title="languageSelectionTitle"
      :is-currency-set="isCurrencySet"
      @close="isModalOpen = false"
      @save="handleCurrencyAndLanguageSelectionChange"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { Auth } from 'aws-amplify'
import CurrencyAndLanguageSelectionModal from '@/components/modals/CurrencyAndLanguageSelectionModal'
import Skeleton from '@/components/loader/Skeleton'
import { EventBus, Events } from '~/lib/events'
import { useUser } from '@/lib/hooks'
import { getLanguage, setLanguage, getCurrency, setCurrency } from '~/util'
import CurrencyAndLanguageSelection from '@/content/modals/currency-and-language-selection'

export default {
  name: 'CurrencySelection',
  components: {
    CurrencyAndLanguageSelectionModal,
    Skeleton,
  },

  props: {
    isCurrencySet: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    page: CurrencyAndLanguageSelection,
    currency: null,
    language: null,
    prevCurrency: null,
    prevLanguage: null,
    isModalOpen: false,
    availableLanguages: [],
    validCurrencies: [],
  }),

  computed: {
    currencySelectionTitle() {
      return this.page.currency.title
    },

    currencyOptions() {
      return this.page.currency.options
    },

    currentCurrencyOption() {
      return this.currencyOptions.find(option => option.ISO === this.currency)
    },

    languageSelectionTitle() {
      return this.page.language.title
    },

    languageOptions() {
      return this.page.language.options
    },

    displayLanguage() {
      return this.language ? `${this.language.toUpperCase()}` : ''
    },

    iconComponent() {
      if (!this.currentCurrencyOption?.name) return

      return () =>
        import(
          `@/assets/svg/icons/national-flags/${this.currentCurrencyOption.name}.svg?inline`
        )
    },

    iconAltText() {
      if (!this.currentCurrencyOption?.name) return 'a national flag'

      const country = this.currentCurrencyOption.name.split('-').join(' ')

      return `${country} national flag`
    },

    shouldValidateCurrencyCookie() {
      if (this.$route.query.redirect_status === 'succeeded') {
        return false
      }

      return true
    },
  },

  created() {
    if (!getCurrency()) {
      setCurrency('usd')
    }

    if (!getLanguage()) {
      setLanguage('en')
    }

    this.currency = getCurrency()
    this.language = getLanguage()
  },

  async mounted() {
    this.fetchValidCurrencies()

    this.fetchLanguages()

    await Promise.all([
      this.validateSelectedCurrencyCookie(getCurrency()),
      this.validateSelectedLanguageCookie(getLanguage()),
    ])

    EventBus.$on(Events.CurrencyChange, selectedCurrency => {
      this.validateSelectedCurrencyCookie(selectedCurrency)
    })
  },

  beforeDestroy() {
    EventBus.$off(Events.CurrencyChange, selectedCurrency => {
      this.validateSelectedCurrencyCookie(selectedCurrency)
    })
  },

  methods: {
    ...mapActions({
      fetchCart: 'cart/fetchCart',
      fetchCatalog: 'app/fetchCatalog',
      replaceOrders: 'dashboard/replaceOrders',
      fetchDomainsAvailability: 'domains/fetchDomainsAvailability',
    }),

    async handleCurrencyAndLanguageSelectionChange({ currency, language }) {
      this.isModalOpen = false

      await Promise.all([
        this.handleCurrencySelectionChange(currency),
        this.handleLanguageSelectionChange(language),
      ])
    },

    fetchValidCurrencies() {
      this.currencyOptions.forEach(option => {
        this.validCurrencies.push(option.ISO)
      })
    },

    async handleCurrencySelectionChange(currency) {
      if (this.prevCurrency === currency) return

      try {
        await this.updateCurrencyActions(currency)
      } catch (error) {
        await this.updateCurrencyErrorHandlingActions(error)
      }
    },

    async validateSelectedCurrencyCookie(currencyCookie) {
      if (!this.shouldValidateCurrencyCookie) return

      const isValidCurrency = this.validCurrencies.includes(currencyCookie)

      if (isValidCurrency) {
        if (this.prevCurrency === currencyCookie) return

        try {
          await this.updateCurrencyActions(currencyCookie, true)
        } catch (error) {
          await this.updateCurrencyErrorHandlingActions(error, false)
        }
      } else {
        await this.updateCurrencyErrorHandlingActions(undefined, false)
      }
    },

    async updateCurrencyActions(currency, compareUserCurrency = false) {
      this.currency = currency

      if (currency) {
        setCurrency(this.currency)
      }

      // Replace orders will rely on the cart currency (not working now due to Stripe Limitations)
      if (await this.hasUser()) {
        if (compareUserCurrency) {
          const userCurrency = await this.fetchUserCurrency()

          await Promise.all([this.fetchCart(true), this.fetchCatalog()])

          if (currency === userCurrency) return
        }

        await this.updateUserCurrency(this.currency)

        // Update user currency needs to execute first to ensure the new currency is identical to the Stripe customer currency
        await this.replaceOrders()

        await this.fetchDomainsAvailability()
      }

      if (!compareUserCurrency) {
        await Promise.all([this.fetchCart(true), this.fetchCatalog()])
      }

      this.prevCurrency = this.currency
    },

    async updateCurrencyErrorHandlingActions(error = '', showToast = true) {
      // A workaround for an edge case where there is no error message
      if (error === '') {
        try {
          await this.updateCurrencyActions('usd')
        } catch (error) {
          return this.updateCurrencyErrorHandlingActions(error, false)
        }
      }

      // The error message includes the Stripe customer currency
      const regex = new RegExp('((set.to.))...', 'g')
      const match = error?.message?.match(regex)

      let defaultCurrency = ''
      if (match && match.length > 0 && match[0]) {
        defaultCurrency = match[0].split('set to ')[1]
      }

      const isValidDefaultCurrency = this.validCurrencies.includes(
        defaultCurrency
      )
      if (!isValidDefaultCurrency) {
        defaultCurrency = 'usd'
      }

      showToast && this.$toast.error(error)

      try {
        await this.updateCurrencyActions(defaultCurrency, true)

        this.prevCurrency = defaultCurrency
      } catch (error) {
        this.$toast.error(
          'Something went wrong.  Please try again, or contact support.'
        )

        this.$sentry.captureException(error)
      }
    },

    fetchLanguages() {
      const options = window.Weglot?.options || {
        languages: [{ language_to: 'en' }],
        language_from: 'en',
      }
      this.availableLanguages = [
        ...(options.languages?.map(lang => lang.language_to) || []),
        options.language_from,
      ]
    },

    async handleLanguageSelectionChange(language) {
      if (this.language === language) return

      try {
        await this.updateLanguageActions(language)
      } catch (error) {
        await this.updateLanguageErrorHandlingActions()
      }
    },

    async validateSelectedLanguageCookie(languageCookie) {
      const isAvailableLanguages = this.availableLanguages.includes(
        languageCookie
      )

      if (isAvailableLanguages) {
        if (this.prevLanguage === languageCookie) return

        try {
          await this.updateLanguageActions(languageCookie)
        } catch (error) {
          await this.updateLanguageErrorHandlingActions()
        }
      } else {
        await this.updateLanguageErrorHandlingActions()
      }
    },

    async updateLanguageActions(language) {
      await this.updateUserLanguage(language)

      this.language = language

      this.prevLanguage = this.language

      setLanguage(this.language)

      window.Weglot?.switchTo(this.language)
    },

    async updateLanguageErrorHandlingActions() {
      try {
        await this.updateUserLanguage('en')
      } catch (error) {
        this.$toast.error(
          'Something went wrong.  Please try again, or contact support.'
        )

        this.$sentry.captureException(error)
      }

      this.language = 'en'

      this.prevLanguage = this.language

      setLanguage(this.language)

      window.Weglot?.switchTo(this.language)

      this.$toast.error(
        'Unable to select unavailable language, default to English.'
      )
    },

    async updateUserCurrency(currency) {
      const { updateUser } = useUser(this.$api)

      await updateUser({ currency })
    },

    async updateUserLanguage(language) {
      const { updateUser } = useUser(this.$api)

      if (await this.hasUser()) {
        await updateUser({ language })
      }
    },

    async hasUser() {
      const result = await Auth.currentSession()
        .then(res => res.isValid())
        .catch(() => false)

      return result
    },

    async fetchUserCurrency() {
      const { getUser } = useUser(this.$api)

      const user = await getUser().catch(() => null)

      return user?.currency || null
    },
  },
}
</script>
